import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Send, Smile } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { useAuth } from '../../contexts/AuthContext';
import { useCommentReplies } from '../hooks/useSocialFeedRest';

import { apiCall } from '../../services/apiUtil';
import ReactorsModal from './ReactorsModal';

const REACTIONS = [
  { type: 'like',    emoji: '👍', label: 'Like' },
  { type: 'love',    emoji: '❤️', label: 'Love' },
  { type: 'haha',    emoji: '😂', label: 'Haha' },
  { type: 'sad',     emoji: '😢', label: 'Sad' },
  { type: 'dislike', emoji: '👎', label: 'Dislike' },
];
const REACTION_MAP = Object.fromEntries(REACTIONS.map(r => [r.type, r]));

const CommentWithReplies = ({ comment, formatTime, postId }) => {
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();

  // Reaction state — initialised from comment prop, then tracked locally
  const [userReaction, setUserReaction] = useState(comment.userReaction || null);
  const [reactionCounts, setReactionCounts] = useState(
    comment.reactionCounts || { like: Number(comment.likes) || 0, dislike: 0, love: 0, haha: 0, sad: 0 }
  );

  const [showReplies, setShowReplies] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [newReply, setNewReply] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [reactorsModal, setReactorsModal] = useState(null);

  const { replies, loading: repliesLoading, submitting, addReply } = useCommentReplies(
    comment.id,
    postId,
    showReplies
  );

  // ── Reactions ─────────────────────────────────────────────────────────────────

  const handleReact = async (type) => {
    setShowEmojiPicker(false);
    const prevReaction = userReaction;
    const isToggling = prevReaction === type;

    // Optimistic update
    setUserReaction(isToggling ? null : type);
    setReactionCounts(prev => {
      const next = { ...prev };
      if (prevReaction) next[prevReaction] = Math.max(0, (next[prevReaction] || 0) - 1);
      if (!isToggling) next[type] = (next[type] || 0) + 1;
      return next;
    });

    try {
      if (isToggling) {
        await apiCall(`/posts/${postId}/comments/${comment.id}/react`, { method: 'DELETE' });
      } else {
        await apiCall(`/posts/${postId}/comments/${comment.id}/react`, {
          method: 'POST',
          body: JSON.stringify({ type }),
        });
      }
    } catch (err) {
      // Revert on failure
      setUserReaction(prevReaction);
      setReactionCounts(comment.reactionCounts || { like: Number(comment.likes) || 0, dislike: 0, love: 0, haha: 0, sad: 0 });
      console.error('Failed to react to comment:', err);
    }
  };

  // ── Replies ──────────────────────────────────────────────────────────────────

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim()) return;

    const result = await addReply(newReply);
    if (result.success) {
      setNewReply('');
      setShowReplyInput(false);
      setShowReplies(true); // Auto-show so user sees their new reply
    } else {
      toast.error(`Failed to add reply: ${result.error}`);
    }
  };

  const totalReplies = showReplies ? replies.length : (Number(comment.repliesCount) || 0);

  return (
    <div className="space-y-3">
      {/* Reactors Modal */}
      {reactorsModal && (
        <ReactorsModal
          endpoint={`/posts/${postId}/comments/${comment.id}/reactors`}
          counts={reactionCounts}
          initialTab={reactorsModal.tab}
          onClose={() => setReactorsModal(null)}
        />
      )}
      {/* Main Comment */}
      <div className="flex gap-x-3">
        <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-white">
            {comment.user?.username?.charAt(0)?.toUpperCase() || 'U'}
          </span>
        </div>
        <div className="flex-1">
          <div className={`${theme.card} rounded-2xl px-3 py-2`}>
            <p className={`font-medium text-sm ${theme.text}`}>
              {comment.user?.username || 'Unknown User'}
            </p>
            <p className={`${theme.textSecondary} text-sm`}>{comment.content}</p>
          </div>

          {/* Comment Actions */}
          <div className="flex items-center gap-x-4 mt-1 ml-3">
            <div className="flex items-center gap-x-2 relative">

              {/* Emoji Picker Toggle */}
              <button
                onClick={() => setShowEmojiPicker(prev => !prev)}
                className={`text-xs ${theme.textMuted} hover:text-blue-500 transition-colors font-medium`}
                title="React"
              >
                <Smile className="w-3 h-3" />
              </button>

              {/* Emoji Picker */}
              {showEmojiPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowEmojiPicker(false)} />
                  <div className={`absolute bottom-6 left-0 z-20 ${theme.card} rounded-full shadow-lg px-2 py-1.5 flex gap-x-1 border ${theme.border}`}>
                    {REACTIONS.map(r => (
                      <button
                        key={r.type}
                        onClick={() => handleReact(r.type)}
                        title={r.label}
                        className={`p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all hover:scale-125 text-base ${
                          userReaction === r.type ? 'scale-110 bg-gray-100 dark:bg-gray-700' : ''
                        }`}
                      >
                        {r.emoji}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Reaction button */}
              {(() => {
                const current = userReaction ? REACTION_MAP[userReaction] : null;
                return (
                  <button
                    onClick={() => handleReact(userReaction || 'like')}
                    className={`flex items-center gap-x-1 text-xs transition-colors font-medium ${
                      current ? 'text-blue-500' : `${theme.textMuted} hover:text-blue-500`
                    }`}
                  >
                    <span className="leading-none">{current ? current.emoji : '👍'}</span>
                    <span>{current ? current.label : 'Like'}</span>
                  </button>
                );
              })()}

              {/* Per-type reaction counts — click anywhere opens who-reacted modal */}
              {REACTIONS.filter(r => (reactionCounts[r.type] || 0) > 0).map(r => (
                <button
                  key={r.type}
                  onClick={() => setReactorsModal({ tab: r.type })}
                  title={`See who reacted with ${r.label}`}
                  className={`flex items-center gap-x-0.5 px-1 py-0.5 rounded-full text-xs border transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    userReaction === r.type
                      ? 'text-blue-500 border-blue-300 bg-blue-50 dark:bg-blue-900/20'
                      : `${theme.textMuted} border-gray-200 dark:border-gray-600`
                  }`}
                >
                  <span>{r.emoji}</span>
                  <span className="font-medium">{reactionCounts[r.type]}</span>
                </button>
              ))}

              {/* Reply Button */}
              <button
                onClick={() => setShowReplyInput(prev => !prev)}
                className={`text-xs ${theme.textMuted} hover:text-blue-500 transition-colors font-medium`}
              >
                Reply
              </button>

              {/* View / Hide Replies */}
              {(Number(comment.repliesCount) > 0 || replies.length > 0) && (
                <button
                  onClick={() => setShowReplies(prev => !prev)}
                  className={`text-xs ${theme.textMuted} hover:text-blue-500 transition-colors font-medium`}
                >
                  {showReplies ? 'Hide replies' : `View ${totalReplies} ${totalReplies === 1 ? 'reply' : 'replies'}`}
                </button>
              )}

              <span className={`text-xs ${theme.textMuted}`}>
                {formatTime(comment.createdAt)}
              </span>
            </div>
          </div>

          {/* Reply Input */}
          {showReplyInput && (
            <div className="mt-3 ml-3">
              <form onSubmit={handleSubmitReply} className="flex gap-x-2">
                <div className="w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-white">
                    {currentUser?.firstName?.charAt(0)?.toUpperCase() || currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 flex gap-2">
                  <input
                    value={newReply}
                    onChange={(e) => setNewReply(e.target.value)}
                    placeholder="Write a reply..."
                    className={`flex-1 px-3 py-2 text-sm border ${theme.border} rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input}`}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitReply(e);
                      }
                    }}
                  />
                  <button
                    type="submit"
                    disabled={!newReply.trim() || submitting}
                    className={`${btn.base} ${btn.sm} ${theme.buttonPrimary}`}
                  >
                    {submitting ? (
                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send className="w-3 h-3" />
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Replies List */}
          {showReplies && (
            <div className="mt-3 ml-6 space-y-2">
              {repliesLoading ? (
                <div className="flex justify-center py-2">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : replies.length > 0 ? (
                replies.map(reply => (
                  <div key={String(reply.id || '')} className="flex gap-x-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-white">
                        {reply.user?.username?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className={`${theme.backgroundSecondary} rounded-lg p-2`}>
                        <div className="flex items-center justify-between mb-1">
                          <p className={`font-medium text-xs ${theme.text}`}>
                            {reply.user?.username || 'Unknown User'}
                          </p>
                          <span className={`text-xs ${theme.textMuted}`}>
                            {formatTime(reply.createdAt)}
                          </span>
                        </div>
                        <p className={`${theme.textSecondary} text-xs`}>{reply.content}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className={`text-xs ${theme.textMuted} text-center py-2`}>No replies yet</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentWithReplies;
